// Catálogo cliente de personajes genéricos (LemonSlice on-the-fly).
//
// Solo datos presentables: imagen de card, preview y emoji. Los prompts, la
// voz y los settings de LemonSlice viven en su gemelo server-only
// (`server/application/generic-characters/`) y NUNCA deben llegar al bundle
// cliente. Un test de paridad garantiza que ambos catálogos declaran los
// mismos ids, `enabled` y `featureFlag`. Los nombres/descr. localizados de las cards
// salen de `locales/*/character-selector.json` (`persons.<id>`).
//
// Un fichero por personaje (`<id>.ts`); este index agrega el array y expone los
// helpers. Assets por personaje en `public/generic/<id>/`:
//   - `<id>.png`             → máxima calidad; SOLO para LemonSlice
//                              (agentImageUrl, lo referencia el config server).
//   - `<id>-card.{webp,png}` → variante 368×560 optimizada para las cards.
//
// `enabled` es el kill-switch por personaje. `featureFlag`, cuando existe,
// requiere además una evaluación server-side positiva para el usuario.

import type { Character } from '@/lib/characters'
import type { GenericCharacter } from './types'
import { genericDrilo } from './generic-drilo'
import { genericAstro } from './generic-astro'
import { genericBruja } from './generic-bruja'
import { genericDragona } from './generic-dragona'
import { genericPirata } from './generic-pirata'
import { genericSirena } from './generic-sirena'
import { genericDino } from './generic-dino'
import { genericUnicornio } from './generic-unicornio'
import { genericElfo } from './generic-elfo'
import { genericGenio } from './generic-genio'
import { genericGnomo } from './generic-gnomo'
import { genericMerlin } from './generic-merlin'
import { genericConejoPascua } from './generic-conejo-pascua'
import { genericFantasma } from './generic-fantasma'
import { genericMomia } from './generic-momia'
import { genericFrankenstein } from './generic-frankenstein'
import { genericVampiro } from './generic-vampiro'

export type { GenericCharacter } from './types'

export const GENERIC_CHARACTERS: GenericCharacter[] = [
  genericDrilo,
  genericAstro,
  genericBruja,
  genericDragona,
  genericPirata,
  genericSirena,
  genericDino,
  genericUnicornio,
  genericElfo,
  genericGenio,
  genericGnomo,
  genericMerlin,
  genericConejoPascua,
  genericFantasma,
  genericMomia,
  genericFrankenstein,
  genericVampiro,
]

/** Catálogo estable: excluye pilotos que requieren una flag remota por usuario. */
export const ENABLED_GENERIC_CHARACTERS = GENERIC_CHARACTERS.filter(
  (character) => character.enabled && !character.featureFlag
)

export const GENERIC_CHARACTER_IDS = GENERIC_CHARACTERS.map((character) => character.id)

/**
 * Cuántos genéricos muestra la galería del dashboard antes de "ver más". 5 y no
 * 6: con los 6 clásicos y Papá Noel a dos columnas (su entrada al chat) son 12
 * huecos, que llenan filas completas tanto de 2 (móvil) como de 3 columnas.
 */
export const FEATURED_GENERIC_LIMIT = 5

/**
 * Genéricos habilitados ordenados por `order` (menor primero). Fuente única del
 * orden para la galería del dashboard (recortada) y el popup de selección
 * (todos). No muta el catálogo (`filter` ya devuelve copia).
 */
export function orderedEnabledGenerics(
  catalog: GenericCharacter[] = GENERIC_CHARACTERS,
  enabledFlags: Readonly<Record<string, boolean>> = {}
): GenericCharacter[] {
  return catalog
    .filter(
      (character) =>
        character.enabled &&
        (!character.featureFlag || enabledFlags[character.featureFlag] === true)
    )
    .sort((a, b) => a.order - b.order)
}

/** Predicado por prefijo: cubre también ids genéricos aún no catalogados. */
export function isGenericCharacterId(id: string): boolean {
  return id.startsWith('generic-')
}

export function getGenericCharacterById(id: string): GenericCharacter | undefined {
  return GENERIC_CHARACTERS.find((character) => character.id === id)
}

/**
 * Proyección al shape del catálogo general para que gallery/selector y
 * getCharacterCallRoute traten a los genéricos como a cualquier otro
 * personaje. Siempre vídeo (LemonSlice) y una card por personaje.
 */
export function toCharacter(generic: GenericCharacter): Character {
  return {
    id: generic.id,
    name: generic.id,
    displayName: generic.displayName,
    callType: 'video',
    icon: generic.icon,
    personKey: generic.id,
  }
}
